import React, { useState, useEffect } from 'react';
import {
  MessageCircle, Search, Filter, RefreshCw, ExternalLink, Trash2,
  Bug, Lightbulb, HelpCircle, FileText, Clock, CheckCircle, 
  AlertCircle, TicketCheck, Eye, ChevronDown, ChevronRight,
  ThumbsUp, ThumbsDown, Loader2, X, ArrowRightCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../../services/api';

const CATEGORY_ICONS = {
  general: MessageCircle,
  bug: Bug,
  feature: Lightbulb,
  question: HelpCircle,
  feedback: ThumbsUp,
};

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  open: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-purple-100 text-purple-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-700',
};

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

export default function SupportRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [converting, setConverting] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertData, setConvertData] = useState({ category: 'general', priority: 'medium' });
  
  // Filters
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    search: '',
  });

  useEffect(() => {
    loadRequests();
  }, [filters]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.type && filters.type !== 'all') params.append('type_filter', filters.type);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      
      const response = await api.get(`/feedback?${params.toString()}`);
      setRequests(response.data.items || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error loading requests:', error);
      toast.error('Failed to load support requests');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setAdminNotes(request.admin_notes || '');
    setShowDetailDialog(true);
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedRequest) return;
    
    setUpdatingStatus(true);
    try {
      await api.patch(`/feedback/${selectedRequest.id}`, {
        status: newStatus,
        admin_notes: adminNotes,
      });
      
      toast.success(`Status updated to ${newStatus}`);
      loadRequests();
      
      setSelectedRequest(prev => ({ ...prev, status: newStatus, admin_notes: adminNotes }));
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCreateJiraTicket = async () => {
    if (!selectedRequest) return;
    
    setCreatingTicket(true);
    try {
      const response = await api.post(`/feedback/${selectedRequest.id}/jira-ticket`);
      
      toast.success(`Jira ticket created: ${response.data.ticket_key}`);
      loadRequests();
      
      setSelectedRequest(prev => ({
        ...prev,
        jira_ticket_key: response.data.ticket_key,
        jira_ticket_url: response.data.ticket_url,
        status: 'in_progress',
      }));
    } catch (error) {
      console.error('Error creating Jira ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to create Jira ticket');
    } finally {
      setCreatingTicket(false);
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    
    try {
      await api.delete(`/feedback/${requestId}`);
      toast.success('Request deleted');
      loadRequests();
      setShowDetailDialog(false);
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error('Failed to delete request');
    }
  };

  const handleConvertToSupport = async () => {
    if (!selectedRequest) return;
    
    setConverting(true);
    try {
      const response = await api.post(`/feedback/${selectedRequest.id}/convert-to-support`, convertData);
      
      toast.success('Feedback converted to support request');
      setShowConvertDialog(false);
      setConvertData({ category: 'general', priority: 'medium' });
      loadRequests();
      
      // Update the selected request in the detail dialog
      if (response.data.item) {
        setSelectedRequest(response.data.item);
      }
    } catch (error) {
      console.error('Error converting feedback:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert feedback');
    } finally {
      setConverting(false);
    }
  };

  const filteredRequests = requests.filter(req => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        req.title?.toLowerCase().includes(search) ||
        req.description?.toLowerCase().includes(search) ||
        req.user_email?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="p-6 space-y-6" data-testid="support-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageCircle className="w-7 h-7 text-blue-600" />
            Support Requests
          </h1>
          <p className="text-gray-500 mt-1">Manage feedback and support requests from users</p>
        </div>
        <Button onClick={loadRequests} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total || 0}</div>
            <div className="text-sm text-gray-500">Total Requests</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.feedback_count || 0}</div>
            <div className="text-sm text-gray-500">Feedback</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-600">{stats.support_count || 0}</div>
            <div className="text-sm text-gray-500">Support Requests</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-amber-600">{stats.open_support || 0}</div>
            <div className="text-sm text-gray-500">Open</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats.in_progress || 0}</div>
            <div className="text-sm text-gray-500">In Progress</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by title, description, or email..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={filters.type} onValueChange={(v) => setFilters(prev => ({ ...prev, type: v }))}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="feedback">Feedback</SelectItem>
                <SelectItem value="support">Support</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      <Card>
        <CardHeader>
          <CardTitle>Requests ({filteredRequests.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No requests found</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredRequests.map((req) => {
                const CategoryIcon = CATEGORY_ICONS[req.category] || CATEGORY_ICONS[req.type] || MessageCircle;
                const isSupport = req.type === 'support';
                
                return (
                  <div 
                    key={req.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleViewDetails(req)}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${isSupport ? 'bg-purple-100' : 'bg-blue-100'}`}>
                        <CategoryIcon className={`w-5 h-5 ${isSupport ? 'text-purple-600' : 'text-blue-600'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-gray-900 truncate">{req.title}</h4>
                          <Badge variant="outline" className={STATUS_COLORS[req.status] || STATUS_COLORS.new}>
                            {(req.status || 'new').replace('_', ' ')}
                          </Badge>
                          {req.priority && (
                            <Badge variant="outline" className={PRIORITY_COLORS[req.priority]}>
                              {req.priority}
                            </Badge>
                          )}
                          {req.jira_ticket_key && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">
                              <TicketCheck className="w-3 h-3 mr-1" />
                              {req.jira_ticket_key}
                            </Badge>
                          )}
                          {req.rating && (
                            <Badge variant="outline" className={req.rating === 'up' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}>
                              {req.rating === 'up' ? <ThumbsUp className="w-3 h-3" /> : <ThumbsDown className="w-3 h-3" />}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{req.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                          <span>{req.user_email}</span>
                          <span>•</span>
                          <span>{formatDate(req.created_at)}</span>
                          {req.category && (
                            <>
                              <span>•</span>
                              <span className="capitalize">{req.category}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {(!req.type || req.type === 'feedback') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs text-purple-600 hover:text-purple-800 hover:bg-purple-50 h-7 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRequest(req);
                              setShowConvertDialog(true);
                            }}
                            data-testid={`convert-btn-${req.id}`}
                          >
                            <ArrowRightCircle className="w-3.5 h-3.5 mr-1" />
                            Convert
                          </Button>
                        )}
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle className="text-xl">{selectedRequest?.title}</DialogTitle>
                <DialogDescription className="mt-1">
                  {selectedRequest?.type === 'support' ? 'Support Request' : 'Feedback'} from {selectedRequest?.user_email}
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                {selectedRequest?.status && (
                  <Badge className={STATUS_COLORS[selectedRequest.status]}>
                    {selectedRequest.status.replace('_', ' ')}
                  </Badge>
                )}
                {selectedRequest?.priority && (
                  <Badge className={PRIORITY_COLORS[selectedRequest.priority]}>
                    {selectedRequest.priority}
                  </Badge>
                )}
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-6 py-4">
              {/* Description */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap">
                  {selectedRequest?.description || 'No description provided'}
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Submitted by:</span>
                  <p className="font-medium">{selectedRequest?.user_name || 'Unknown'}</p>
                  <p className="text-gray-500">{selectedRequest?.user_email}</p>
                </div>
                <div>
                  <span className="text-gray-500">Created:</span>
                  <p className="font-medium">{formatDate(selectedRequest?.created_at)}</p>
                </div>
                {selectedRequest?.page_url && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Page URL:</span>
                    <p className="font-medium text-blue-600 truncate">{selectedRequest.page_url}</p>
                  </div>
                )}
              </div>

              {/* Jira Ticket */}
              {selectedRequest?.jira_ticket_key && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TicketCheck className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-900">Jira Ticket: {selectedRequest.jira_ticket_key}</span>
                    </div>
                    <a
                      href={selectedRequest.jira_ticket_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                    >
                      Open in Jira <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Admin Notes</h4>
                <Textarea
                  placeholder="Add notes about this request..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="min-h-[80px]"
                />
              </div>

              {/* Screenshot */}
              {selectedRequest?.screenshot && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Screenshot</h4>
                  <img 
                    src={selectedRequest.screenshot} 
                    alt="Screenshot" 
                    className="max-w-full rounded-lg border"
                  />
                </div>
              )}
            </div>
          </ScrollArea>

          <DialogFooter className="border-t pt-4 flex-wrap gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleDelete(selectedRequest?.id)}
              data-testid="delete-request-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
            
            <div className="flex-1" />

            {/* Convert Feedback to Support Request button */}
            {(!selectedRequest?.type || selectedRequest?.type === 'feedback') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowConvertDialog(true)}
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="convert-to-support-btn"
              >
                <ArrowRightCircle className="w-4 h-4 mr-1.5" />
                Convert to Support Request
              </Button>
            )}
            
            {!selectedRequest?.jira_ticket_key && selectedRequest?.type === 'support' && (
              <Button
                variant="outline"
                onClick={handleCreateJiraTicket}
                disabled={creatingTicket}
              >
                {creatingTicket ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <TicketCheck className="w-4 h-4 mr-2" />
                )}
                Create Jira Ticket
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={updatingStatus}>
                  {updatingStatus ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Clock className="w-4 h-4 mr-2" />
                  )}
                  Update Status
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleUpdateStatus('open')}>
                  <AlertCircle className="w-4 h-4 mr-2 text-amber-500" />
                  Open
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleUpdateStatus('in_progress')}>
                  <Clock className="w-4 h-4 mr-2 text-purple-500" />
                  In Progress
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleUpdateStatus('resolved')}>
                  <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                  Resolved
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleUpdateStatus('closed')}>
                  <X className="w-4 h-4 mr-2 text-gray-500" />
                  Closed
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Support Request Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="max-w-md" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightCircle className="w-5 h-5 text-purple-600" />
              Convert to Support Request
            </DialogTitle>
            <DialogDescription>
              This will convert the feedback "{selectedRequest?.title}" into a support request with a category and priority.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Category</Label>
              <Select 
                value={convertData.category} 
                onValueChange={(v) => setConvertData(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger data-testid="convert-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-blue-500" />
                      General Support
                    </div>
                  </SelectItem>
                  <SelectItem value="bug">
                    <div className="flex items-center gap-2">
                      <Bug className="w-4 h-4 text-red-500" />
                      Bug Report
                    </div>
                  </SelectItem>
                  <SelectItem value="feature">
                    <div className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-amber-500" />
                      Feature Request
                    </div>
                  </SelectItem>
                  <SelectItem value="question">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-purple-500" />
                      Question
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Priority</Label>
              <Select 
                value={convertData.priority} 
                onValueChange={(v) => setConvertData(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger data-testid="convert-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-gray-500" />
                      Low
                    </div>
                  </SelectItem>
                  <SelectItem value="medium">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      Medium
                    </div>
                  </SelectItem>
                  <SelectItem value="high">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-orange-500" />
                      High
                    </div>
                  </SelectItem>
                  <SelectItem value="urgent">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                      Urgent
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConvertToSupport}
              disabled={converting}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="convert-confirm-btn"
            >
              {converting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRightCircle className="w-4 h-4 mr-2" />
              )}
              Convert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
